import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import QRCode from "qrcode";
import { Button, Input, MessageBox } from "@/components/ui";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/useToast";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
import { HOME_PAGE, LOGIN_PAGE } from "@/router/paths";
import MfaService from "@/services/mfa";
import { services } from "@/services/registry";
const mfaVerifySchema = z.object({
    code: z.string().min(6, "Code must be 6 digits").max(6, "Code must be 6 digits").regex(/^[0-9]+$/, "Code must contain only numbers")
});
export default function MfaSetupPage() {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        resolver: zodResolver(mfaVerifySchema)
    });
    const { challengeData, mfaSetupRequired, clearMfaSetupRequired, user } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();
    // State for MFA setup
    const [secretCode, setSecretCode] = useState(null);
    const [session, setSession] = useState(null);
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [setupError, setSetupError] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(null);
    // Ref to prevent double-calling setup (React Strict Mode calls effects twice)
    const setupInitiatedRef = useRef(false);
    // Determine which flow we're using:
    // 1. Challenge flow: challengeData?.session exists (Cognito MFA_SETUP challenge)
    // 2. Authenticated flow: mfaSetupRequired is true (org-enforced)
    const useChallengeFlow = !!challengeData?.session;
    const useAuthenticatedFlow = mfaSetupRequired && !useChallengeFlow;
    // Get email from challenge data or authenticated user
    const email = challengeData?.email || user?.email || "user";
    // Initiate MFA setup on mount
    useEffect(()=>{
        const initSetup = async ()=>{
            if (setupInitiatedRef.current) return;
            // For challenge flow, use the session
            if (useChallengeFlow && challengeData?.session) {
                setupInitiatedRef.current = true;
                setIsSettingUp(true);
                setSetupError(null);
                try {
                    const result = await services.mfaService.setupChallenge({
                        session: challengeData.session
                    });
                    setSecretCode(result.secretCode);
                    setSession(result.session);
                } catch (error) {
                    const message = error instanceof Error ? error.message : "Failed to initiate MFA setup";
                    setSetupError(message);
                    setupInitiatedRef.current = false;
                } finally{
                    setIsSettingUp(false);
                }
                return;
            }
            // For authenticated flow (org-enforced), use the authenticated setup endpoint
            if (useAuthenticatedFlow) {
                setupInitiatedRef.current = true;
                setIsSettingUp(true);
                setSetupError(null);
                try {
                    const result = await services.mfaService.setup();
                    setSecretCode(result.secretCode);
                // No session needed for authenticated flow - we use verify() not verifyChallenge()
                } catch (error) {
                    const message = error instanceof Error ? error.message : "Failed to initiate MFA setup";
                    setSetupError(message);
                    setupInitiatedRef.current = false;
                } finally{
                    setIsSettingUp(false);
                }
                return;
            }
            // If neither flow applies, redirect to login
            if (!useChallengeFlow && !useAuthenticatedFlow) {
                navigate(LOGIN_PAGE);
            }
        };
        initSetup();
    }, [
        challengeData,
        useChallengeFlow,
        useAuthenticatedFlow,
        navigate
    ]);
    // Generate QR code when we have the secret
    useEffect(()=>{
        if (secretCode && email) {
            const otpAuthUrl = MfaService.generateOtpAuthUrl(secretCode, email);
            QRCode.toDataURL(otpAuthUrl, {
                width: 200,
                margin: 2,
                color: {
                    dark: "#000000",
                    light: "#FFFFFF"
                }
            }).then(setQrCodeDataUrl).catch(()=>{
                toast.error("Failed to generate QR code");
            });
        }
    }, [
        secretCode,
        email,
        toast
    ]);
    const onSubmit = async (data)=>{
        if (!email) return;
        setIsVerifying(true);
        setVerifyError(null);
        try {
            if (useChallengeFlow && session) {
                // Challenge flow: use verifyChallenge
                const result = await services.mfaService.verifyChallenge({
                    session,
                    email,
                    code: data.code
                });
                if (result.success) {
                    toast.success("MFA has been enabled successfully!");
                    // Fetch session data to update auth state
                    window.location.href = HOME_PAGE;
                }
            } else if (useAuthenticatedFlow) {
                // Authenticated flow: use verify
                const result = await services.mfaService.verify({
                    code: data.code
                });
                if (result.success) {
                    toast.success("MFA has been enabled successfully!");
                    clearMfaSetupRequired();
                    window.location.href = HOME_PAGE;
                }
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to verify MFA code";
            setVerifyError(message);
        } finally{
            setIsVerifying(false);
        }
    };
    const handleRetry = ()=>{
        setSecretCode(null);
        setSession(null);
        setSetupError(null);
        setQrCodeDataUrl(null);
        setupInitiatedRef.current = false;
    };
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Set Up Two-Factor Authentication",
        subtitle: useAuthenticatedFlow ? "Your organization requires two-factor authentication. Please set it up to continue." : "Two-factor authentication is required to access your account.",
        cardClassName: "w-[500px]",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full",
            children: [
                isSettingUp && /*#__PURE__*/ _jsx("div", {
                    className: "text-center py-8",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-600",
                        children: "Setting up MFA..."
                    })
                }),
                setupError && /*#__PURE__*/ _jsxs("div", {
                    className: "w-full",
                    children: [
                        /*#__PURE__*/ _jsx(MessageBox, {
                            message: setupError,
                            type: "error"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "simple",
                            color: "navy-90",
                            className: "w-full mt-4",
                            onClick: handleRetry,
                            children: "Try Again"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "button",
                            variant: "simple",
                            color: "navy-light",
                            className: "w-full mt-2",
                            onClick: ()=>navigate(LOGIN_PAGE),
                            children: "Back to Login"
                        })
                    ]
                }),
                secretCode && !setupError && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-gray-600 mb-6 text-center",
                            children: "Scan this QR code with your authenticator app (like Google Authenticator or Authy), then enter the 6-digit code to verify."
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mb-6 p-4 bg-white border border-gray-200 rounded-lg flex justify-center",
                            children: qrCodeDataUrl ? /*#__PURE__*/ _jsx("img", {
                                src: qrCodeDataUrl,
                                alt: "MFA QR Code",
                                className: "w-[200px] h-[200px]"
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "w-[200px] h-[200px] flex items-center justify-center bg-gray-100",
                                children: /*#__PURE__*/ _jsx("p", {
                                    className: "text-gray-500 text-sm",
                                    children: "Loading QR code..."
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            className: "text-sm text-blue-600 hover:text-blue-800 mb-4",
                            onClick: ()=>setShowManualEntry(!showManualEntry),
                            children: showManualEntry ? "Hide manual entry code" : "Can't scan? Enter code manually"
                        }),
                        showManualEntry && /*#__PURE__*/ _jsxs("div", {
                            className: "w-full mb-6 p-4 bg-gray-50 rounded-lg",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-gray-500 mb-2",
                                    children: "Manual entry code:"
                                }),
                                /*#__PURE__*/ _jsx("code", {
                                    className: "text-sm font-mono break-all select-all",
                                    children: secretCode
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("form", {
                            onSubmit: handleSubmit(onSubmit),
                            className: "w-full flex flex-col gap-4",
                            children: [
                                verifyError && /*#__PURE__*/ _jsx(MessageBox, {
                                    message: verifyError,
                                    type: "error"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    label: "Verification Code",
                                    type: "text",
                                    inputMode: "numeric",
                                    placeholder: "Enter 6-digit code",
                                    maxLength: 6,
                                    autoComplete: "one-time-code",
                                    ...register("code"),
                                    errors: errors.code
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "submit",
                                    variant: "simple",
                                    color: "navy-90",
                                    className: "w-full",
                                    disabled: isSubmitting || isVerifying,
                                    children: isSubmitting || isVerifying ? "Verifying..." : "Enable MFA"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "simple",
                                    color: "navy-light",
                                    className: "w-full",
                                    onClick: ()=>navigate(LOGIN_PAGE),
                                    children: "Back to Login"
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-6 text-center",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-500",
                        children: "Two-factor authentication adds an extra layer of security to your account."
                    })
                })
            ]
        })
    });
}
